import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { ApiResponse, DocumentTemplate, DocumentType, TemplateMapping } from '../types'
import { apiService } from './api.service'

class DocumentTypeService {
  private _listAllCache: Promise<ApiResponse<DocumentType>> | null = null

  async getDocumentTypes(params?: { page?: number; page_size?: number }): Promise<ApiResponse<DocumentType>> {
    const response = await apiService.get<ApiResponse<DocumentType>>('document_types/', { params })
    return response.data
  }

  async listAll(): Promise<ApiResponse<DocumentType>> {
    if (!this._listAllCache) {
      this._listAllCache = apiService
        .get<ApiResponse<DocumentType>>('document_types/', {
          params: { page: 1, page_size: 100000 },
        })
        .then((response) => response.data)
    }
    return this._listAllCache
  }

  clearCache(): void {
    this._listAllCache = null
  }

  async getDocumentType(id: number): Promise<DocumentType> {
    const response = await apiService.get<DocumentType>(`document_types/${id}/`)
    return response.data
  }

  async createDocumentType(data: Partial<DocumentType>): Promise<DocumentType> {
    this.clearCache()
    const response = await apiService.post<DocumentType>('document_types/', data)
    return response.data
  }

  async updateDocumentType(id: number, data: Partial<DocumentType>): Promise<DocumentType> {
    this.clearCache()
    const response = await apiService.patch<DocumentType>(`document_types/${id}/`, data)
    return response.data
  }

  async deleteDocumentType(id: number): Promise<void> {
    this.clearCache()
    await apiService.delete(`document_types/${id}/`)
  }

  // Methods for ManagementList compatibility
  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    includeDocumentCount?: boolean,
    extraParams?: any
  ): Promise<ApiResponse<DocumentType>> {
    const params: any = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true, // Include full permission fields
    }

    if (sortField) {
      const prefix = sortReverse ? '-' : ''
      params.ordering = `${prefix}${sortField}`
    }

    if (nameFilter) {
      params.name__icontains = nameFilter
    }

    if (includeDocumentCount) {
      params.include_document_count = true
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    return this.getDocumentTypes(params)
  }

  async delete(documentType: DocumentType): Promise<void> {
    return this.deleteDocumentType(documentType.id)
  }

  // Alias methods for EditDialog compatibility
  async create(data: Partial<DocumentType>): Promise<DocumentType> {
    return this.createDocumentType(data)
  }

  async update(id: number, data: Partial<DocumentType>): Promise<DocumentType> {
    return this.updateDocumentType(id, data)
  }

  // Bulk edit for permissions
  async bulkEditObjects(objects: number[], operation: string, permissions?: any, merge?: boolean): Promise<void> {
    const params: any = {
      objects,
      object_type: 'document_types',
      operation,
    }

    if (operation === 'set_permissions' && permissions) {
      params.owner = permissions.owner
      params.permissions = permissions.set_permissions
      params.merge = merge
    }

    await apiService.post('bulk_edit_objects/', params)
  }

  async extractTemplateContent(id: number, file: File): Promise<{ content: string }> {
    const formData = new FormData()
    formData.append('document', file)
    const response = await apiService.post<{ content: string }>(
      `document_types/${id}/extract_template_content/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  async previewMapping(
    id: number,
    content: string,
    mappings: { custom_field_id: number; pattern: string }[]
  ): Promise<{ values: Record<number, string> }> {
    const response = await apiService.post<{ values: Record<number, string> }>(
      `document_types/${id}/preview_mapping/`,
      { content, mappings }
    )
    return response.data
  }

  async updateTemplateMapping(id: number, templateMapping: TemplateMapping): Promise<DocumentType> {
    this.clearCache()
    return this.updateDocumentType(id, { template_mapping: templateMapping })
  }

  async getWordTemplates(id: number): Promise<DocumentTemplate[]> {
    const response = await apiService.get<DocumentTemplate[]>(
      `document_types/${id}/word_templates/`,
    )
    return response.data
  }

  async uploadWordTemplate(
    id: number,
    file: File,
    options?: { name?: string; templateId?: number; mappings?: Record<string, number> },
  ): Promise<DocumentTemplate> {
    const formData = new FormData()
    formData.append('file', file)
    if (options?.name) formData.append('name', options.name)
    if (options?.templateId) formData.append('template_id', String(options.templateId))
    if (options?.mappings) formData.append('mappings', JSON.stringify(options.mappings))
    this.clearCache()
    const response = await apiService.post<DocumentTemplate>(
      `document_types/${id}/word_templates/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  }

  async updateWordTemplate(
    id: number,
    templateId: number,
    data: { name?: string; mappings: Record<string, number> },
  ): Promise<DocumentTemplate> {
    this.clearCache()
    const response = await apiService.post<DocumentTemplate>(
      `document_types/${id}/word_templates/`,
      { template_id: templateId, ...data },
    )
    return response.data
  }

  async refreshWordTemplate(id: number, templateId: number): Promise<DocumentTemplate> {
    this.clearCache()
    const response = await apiService.post<DocumentTemplate>(
      `document_types/${id}/word_templates/refresh/`,
      { template_id: templateId },
    )
    return response.data
  }

  async deleteWordTemplate(id: number, templateId: number): Promise<void> {
    this.clearCache()
    await apiService.post(`document_types/${id}/word_templates/delete/`, { template_id: templateId })
  }

  async uploadTemplateSample(id: number, file: File): Promise<{
    sample_id: number
    page_count: number
    tokens_available: boolean
    dms_upload_task_id?: string
    error?: string
  }> {
    const formData = new FormData()
    formData.append('document', file)
    const response = await apiService.post<{
      sample_id: number
      page_count: number
      tokens_available: boolean
      dms_upload_task_id?: string
      error?: string
    }>(`document_types/${id}/template/sample/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async reprocessTemplateSample(id: number, sampleId: number): Promise<{
    sample_id: number
    page_count: number
    tokens_available: boolean
  }> {
    const response = await apiService.post<{
      sample_id: number
      page_count: number
      tokens_available: boolean
    }>(`document_types/${id}/template/sample_reprocess/`, { sample_id: sampleId })
    return response.data
  }

  async getTemplateSampleInfo(id: number, sampleId: number): Promise<{
    sample_id: number
    page_count: number
    pages: { page_index: number; token_count: number }[]
  }> {
    const response = await apiService.get<{
      sample_id: number
      page_count: number
      pages: { page_index: number; token_count: number }[]
    }>(`document_types/${id}/template/sample_info/`, {
      params: { sample_id: sampleId },
    })
    return response.data
  }

  async fetchTemplateSampleFile(id: number, sampleId: number): Promise<Blob> {
    const response = await apiService.get<Blob>(
      `document_types/${id}/template/sample_file/`,
      { params: { sample_id: sampleId }, responseType: 'blob' }
    )
    return response.data
  }

  async getTemplateRules(id: number, sampleId?: number): Promise<{
    rules: {
      id: number
      custom_field_id: number
      custom_field_name: string
      page_index: number
      anchor_text: string
      value_preview?: string
      value_bbox?: { x0: number; y0: number; x1: number; y1: number }
      mapped_bbox?: { x0: number; y0: number; x1: number; y1: number }
      preview_page_index?: number
    }[]
  }> {
    const response = await apiService.get<{
      rules: {
        id: number
        custom_field_id: number
        custom_field_name: string
        page_index: number
        anchor_text: string
        value_preview?: string
        value_bbox?: { x0: number; y0: number; x1: number; y1: number }
        mapped_bbox?: { x0: number; y0: number; x1: number; y1: number }
        preview_page_index?: number
      }[]
    }>(`document_types/${id}/template/rules/`, {
      params: sampleId ? { sample_id: sampleId } : undefined,
    })
    return response.data
  }

  async getTemplateReferenceDocument(id: number): Promise<{
    template_document_id?: number
    template_document_title?: string | null
    template_sample_id?: number
    sample_id?: number
  }> {
    const response = await apiService.get<{
      template_document_id?: number
      template_document_title?: string | null
      template_sample_id?: number
      sample_id?: number
    }>(`document_types/${id}/template/reference_document/`)
    return response.data
  }

  async setTemplateReferenceDocument(
    id: number,
    documentId: number
  ): Promise<{
    sample_id: number
    page_count: number
    tokens_available: boolean
    template_document_id: number
    template_document_title?: string
  }> {
    const response = await apiService.post<{
      sample_id: number
      page_count: number
      tokens_available: boolean
      template_document_id: number
      template_document_title?: string
    }>(`document_types/${id}/template/reference_document/`, { document_id: documentId })
    return response.data
  }

  async createTemplateRule(
    id: number,
    data: {
      sample_id: number
      custom_field_id: number
      page_index: number
      value_bbox: { x0: number; y0: number; x1: number; y1: number }
    }
  ): Promise<{
    rule_id: number
    anchor_text: string
    value_preview: string
    confidence: number
    ocr_fallback_used?: boolean
  }> {
    const response = await apiService.post<{
      rule_id: number
      anchor_text: string
      value_preview: string
      confidence: number
      ocr_fallback_used?: boolean
    }>(`document_types/${id}/template/rules/`, data)
    return response.data
  }

  async deleteTemplateRule(id: number, ruleId: number): Promise<void> {
    await apiService.post(`document_types/${id}/template/rule/delete/`, { rule_id: ruleId })
  }

  async activateTemplate(id: number, active: boolean): Promise<{ template_active: boolean }> {
    const response = await apiService.post<{ template_active: boolean }>(
      `document_types/${id}/template/activate/`,
      { active }
    )
    return response.data
  }
}

export const documentTypeService = new DocumentTypeService()
